import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCreditCard,
    faCheckCircle,
    faTimesCircle,
    faInfoCircle,
    faEnvelope,
    faBuilding,
    faStore,
    faUsers,
    faList,
    faRocket,
    faGem,
    faBriefcase,
    faArrowRight,
    faUser,
    faComment,
    faPerson,
    faArrows,
    faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import request from '../../../../request';
import Notify from '../../../compartidos/Notify';
import { ProgressBar, Badge, Card, Table, Modal, Form, Button, Spinner } from 'react-bootstrap';

const MySubscription = ({ ipserv }) => {
    const [subData, setSubData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        contactName: '',
        contactEmail: '',
        comment: ''
    });

    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

    useEffect(() => {
        fetchSubStatus();
    }, []);

    const fetchSubStatus = () => {
        setLoading(true);
        request.Get(ipserv + 'platform/subscription-status', {}, (r) => {
            if (r.success) {
                setSubData(r);
                // Pre-fill email if available
                setFormData(prev => ({ ...prev, contactEmail: r.plan.AdminEmail || '' }));
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al obtener datos de suscripción' });
            }
            setLoading(false);
        });
    };

    const handleSelectPlan = (plan) => {
        setSelectedPlan(plan);
        setShowModal(true);
    };

    const handleSubmitRequest = (e) => {
        e.preventDefault();
        setSubmitting(true);

        const payload = {
            ...formData,
            planId: selectedPlan.id,
            planName: selectedPlan.name
        };

        request.Post(ipserv + 'platform/request-plan-change', payload, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Solicitud enviada correctamente. Nos contactaremos pronto.' });
                setShowModal(false);
                setFormData({ ...formData, comment: '' });
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al enviar solicitud' });
            }
            setSubmitting(false);
        });
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center p-5" style={{ height: '400px' }}>
                <Spinner animation="grow" variant="primary" />
            </div>
        );
    }

    if (!subData) return null;

    const { plan, usage, isExpired } = subData;

    const UsageCard = ({ title, count, max, icon, gradient, color }) => {
        const percentage = Math.min((count / max) * 100, 100);
        const isNearLimit = percentage >= 80;
        const isFull = percentage >= 100;

        return (
            <motion.div
                whileHover={{ y: -5 }}
                className="turnero-card-premium p-0 overflow-hidden h-100"
                style={{ border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}
            >
                <div className="p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="usage-icon-box" style={{ background: gradient }}>
                            <FontAwesomeIcon icon={icon} />
                        </div>
                        <div className="text-right">
                            <div className="h4 font-weight-bold m-0" style={{ color: isFull ? '#ef4444' : isNearLimit ? '#f59e0b' : 'var(--text-main)' }}>
                                {count} <span className="h6 text-muted">/ {max}</span>
                            </div>
                        </div>
                    </div>
                    <h6 className="text-muted font-weight-bold mb-3 text-uppercase small">{title}</h6>
                    <div className="custom-progress-container">
                        <div
                            className="custom-progress-bar"
                            style={{
                                width: `${percentage}%`,
                                background: isFull ? 'linear-gradient(90deg, #ef4444, #991b1b)' : gradient
                            }}
                        />
                    </div>
                    <div className="d-flex justify-content-between mt-2">
                        <small className="text-muted tiny">{percentage.toFixed(0)}% Utilizado</small>
                        {isFull && <small className="text-danger tiny font-weight-bold">LÍMITE ALCANZADO</small>}
                    </div>
                </div>
            </motion.div>
        );
    };

    const plans = [
        {
            id: 1,
            name: 'Trial',
            icon: faRocket,
            price: 'Gratis',
            color: '#64748b',
            gradient: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            features: ['1 Empresa', '1 Sucursal', '1 Staff', '1 Sector', 'Modo Lectura al vencer'],
            active: plan.PlanId === 1
        },
        {
            id: 2,
            name: 'Premium',
            icon: faGem,
            price: 'Suscripción',
            color: '#6366f1',
            gradient: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
            features: ['3 Empresas', '3 Sucursales', '3 Staff', '3 Sectores', 'Soporte Video', 'Multi-Sector'],
            active: plan.PlanId === 2,
            popular: true
        },
        {
            id: 3,
            name: 'Business',
            icon: faBriefcase,
            price: 'Corporativo',
            color: '#a855f7',
            gradient: 'linear-gradient(135deg, #a855f7 0%, #7e22ce 100%)',
            features: ['5 Empresas', '10 Sucursales', '10 Staff', '10 Sectores', 'Soporte Prioritario', 'Personalización'],
            active: plan.PlanId === 3
        }
    ];

    return (
        <div className="subscription-container p-2">
            <header className="d-flex justify-content-between align-items-center mb-5 animate-in">
                <div>
                    <h2 className="turnero-title mb-1">Mi Suscripción</h2>
                    <p className="text-muted m-0">Gestiona los límites y expande tu plataforma</p>
                </div>
                <div className="plan-status-badge">
                    <div className="status-dot" style={{ background: isExpired ? '#ef4444' : '#10b981' }}></div>
                    <span className="font-weight-bold">PLAN {plan.PlanNombre}</span>
                    {isExpired && <span className="ml-2 badge badge-danger">VENCIDO</span>}
                </div>
            </header>

            {isExpired && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="alert-premium-danger mb-5 d-flex align-items-center p-4"
                >
                    <div className="alert-icon-circle mr-4">
                        <FontAwesomeIcon icon={faInfoCircle} />
                    </div>
                    <div>
                        <h5 className="m-0 font-weight-bold">Tu periodo de prueba ha expirado</h5>
                        <p className="m-0 opacity-80">El sistema se encuentra en modo lectura. Selecciona un plan a continuación para reactivar todas las funciones.</p>
                    </div>
                </motion.div>
            )}

            <div className="row g-4 mb-5 animate-in" style={{ animationDelay: '0.1s' }}>
                <div className="col-md-3">
                    <UsageCard title="Empresas" count={usage.companies} max={plan.MaxEmpresas} icon={faBuilding} gradient="linear-gradient(135deg, #6366f1 0%, #a855f7 100%)" />
                </div>
                <div className="col-md-3">
                    <UsageCard title="Sucursales" count={usage.branches} max={plan.MaxSucursales} icon={faStore} gradient="linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)" />
                </div>
                <div className="col-md-3">
                    <UsageCard title="Personal Staff" count={usage.staff} max={plan.MaxStaff} icon={faUsers} gradient="linear-gradient(135deg, #10b981 0%, #3b82f6 100%)" />
                </div>
                <div className="col-md-3">
                    <UsageCard title="Conexiones" count={usage.connections} max={plan.MaxConnections} icon={faList} gradient="linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)" />
                </div>


                <div className="col-md-3">
                    <UsageCard title="Perfiles" count={usage.perfiles} max={plan.MaxPerfiles} icon={faPerson} gradient="linear-gradient(135deg, #d0f112ff 0%, #44efd2ff 100%)" />
                </div>

                <div className="col-md-3">
                    <UsageCard title="Consultas Automáticas" count={usage.querys} max={plan.MaxQuerys} icon={faComment} gradient="linear-gradient(135deg, #08ddddff 0%, #2948dbff 100%)" />
                </div>

                <div className="col-md-3">
                    <UsageCard title="Modulos de Menú" count={usage.modules} max={plan.MaxModules} icon={faArrows} gradient="linear-gradient(135deg, #ffdd75ff 0%, #ffe4a0ff 100%)" />
                </div>

                <div className="col-md-3">
                    <UsageCard title="Grupos de Menu" count={usage.groupss} max={plan.MaxGrupos} icon={faLayerGroup} gradient="linear-gradient(135deg, #e32cff8e 0%, #d391ffc3 100%)" />
                </div>
            </div>

            <div className="mb-5">
                <h4 className="font-weight-bold mb-4 d-flex align-items-center">
                    <FontAwesomeIcon icon={faRocket} className="text-primary mr-3" />
                    Planes Disponibles
                </h4>
                <div className="row g-4">
                    {plans.map((p, idx) => (
                        <div key={p.id} className="col-lg-4">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 + idx * 0.1 }}
                                className={`plan-card-premium ${p.active ? 'active' : ''} ${p.popular ? 'popular' : ''}`}
                            >
                                {p.popular && <div className="popular-tag">MÁS ELEGIDO</div>}
                                <div className="plan-header text-center mb-4">
                                    <div className="plan-icon-box mb-3" style={{ background: p.gradient }}>
                                        <FontAwesomeIcon icon={p.icon} />
                                    </div>
                                    <h3 className="font-weight-bold m-0">{p.name}</h3>
                                    <div className="plan-price mt-2">{p.price}</div>
                                </div>
                                <div className="plan-features mb-4">
                                    {p.features.map((f, i) => (
                                        <div key={i} className="feature-item">
                                            <FontAwesomeIcon icon={faCheckCircle} className="mr-2 text-primary opacity-50" />
                                            <span>{f}</span>
                                        </div>
                                    ))}
                                </div>
                                <button
                                    className={`btn-plan-select ${p.active ? 'current' : ''}`}
                                    onClick={() => !p.active && handleSelectPlan(p)}
                                    disabled={p.active}
                                >
                                    {p.active ? 'TU PLAN ACTUAL' : 'SOLICITAR PLAN'}
                                    {!p.active && <FontAwesomeIcon icon={faArrowRight} className="ml-2" />}
                                </button>
                            </motion.div>
                        </div>
                    ))}
                </div>
            </div>

            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                centered
                contentClassName="turnero-modal-premium"
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="font-weight-bold">Solicitar Plan {selectedPlan?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="text-muted small mb-4">
                        Completa tus datos y un asesor se pondrá en contacto contigo para coordinar la activación del plan solicitado.
                    </p>
                    <Form onSubmit={handleSubmitRequest}>
                        <Form.Group className="mb-3">
                            <Form.Label className="tiny text-muted mb-1 text-uppercase font-weight-bold">Nombre de Contacto</Form.Label>
                            <div className="input-with-icon">
                                <FontAwesomeIcon icon={faUser} className="input-icon" />
                                <Form.Control
                                    type="text"
                                    placeholder="Tu nombre completo"
                                    className="turnero-input"
                                    required
                                    value={formData.contactName}
                                    onChange={e => setFormData({ ...formData, contactName: e.target.value })}
                                />
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="tiny text-muted mb-1 text-uppercase font-weight-bold">Email de Contacto</Form.Label>
                            <div className="input-with-icon">
                                <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                                <Form.Control
                                    type="email"
                                    placeholder="correo@ejemplo.com"
                                    className="turnero-input"
                                    required
                                    value={formData.contactEmail}
                                    onChange={e => setFormData({ ...formData, contactEmail: e.target.value })}
                                />
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label className="tiny text-muted mb-1 text-uppercase font-weight-bold">Comentarios Adicionales</Form.Label>
                            <div className="input-with-icon">
                                <FontAwesomeIcon icon={faComment} className="input-icon" style={{ top: '15px' }} />
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Cuéntanos más sobre tus necesidades..."
                                    className="turnero-input"
                                    value={formData.comment}
                                    onChange={e => setFormData({ ...formData, comment: e.target.value })}
                                />
                            </div>
                        </Form.Group>
                        <button
                            type="submit"
                            className="btn-premium-solid w-100 py-3"
                            disabled={submitting}
                        >
                            {submitting ? <Spinner animation="border" size="sm" /> : 'ENVIAR SOLICITUD'}
                        </button>
                    </Form>
                </Modal.Body>
            </Modal>

            <style>{`
                .usage-icon-box {
                    width: 45px;
                    height: 45px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 1.2rem;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
                }
                .custom-progress-container {
                    width: 100%;
                    height: 8px;
                    background: rgba(255,255,255,0.05);
                    border-radius: 10px;
                    overflow: hidden;
                }
                .custom-progress-bar {
                    height: 100%;
                    border-radius: 10px;
                    transition: width 1s ease-in-out;
                }
                .plan-status-badge {
                    background: var(--turnero-glass);
                    padding: 8px 16px;
                    border-radius: 30px;
                    border: 1px solid var(--turnero-glass-border);
                    display: flex;
                    align-items: center;
                    font-size: 0.9rem;
                }
                .status-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    margin-right: 10px;
                    box-shadow: 0 0 8px currentColor;
                }
                .plan-card-premium {
                    background: #ffffff;
                    border: 1px solid var(--border-color);
                    border-radius: 24px;
                    padding: 40px;
                    height: 100%;
                    transition: all 0.3s ease;
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    box-shadow: var(--shadow-sm);
                }
                .plan-card-premium:hover {
                    transform: translateY(-10px);
                    border-color: var(--primary);
                    background: #ffffff;
                    box-shadow: var(--shadow-premium);
                }
                .plan-card-premium.active {
                    border: 2px solid var(--primary);
                }
                .plan-card-premium.popular {
                    border-color: #6366f1;
                }
                .popular-tag {
                    position: absolute;
                    top: 20px;
                    right: 20px;
                    background: linear-gradient(90deg, #6366f1, #a855f7);
                    color: white;
                    font-size: 0.7rem;
                    font-weight: 800;
                    padding: 4px 12px;
                    border-radius: 20px;
                    letter-spacing: 0.5px;
                }
                .plan-icon-box {
                    width: 60px;
                    height: 60px;
                    border-radius: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 1.8rem;
                    margin: 0 auto;
                }
                .plan-price {
                    font-size: 1.2rem;
                    font-weight: 700;
                    color: var(--primary);
                }
                .plan-features {
                    flex-grow: 1;
                }
                .feature-item {
                    display: flex;
                    align-items: center;
                    margin-bottom: 12px;
                    font-size: 0.9rem;
                    color: var(--text-muted);
                }
                .btn-plan-select {
                    width: 100%;
                    padding: 12px;
                    border-radius: 12px;
                    border: 1px solid var(--primary);
                    background: transparent;
                    color: var(--primary);
                    font-weight: 700;
                    transition: all 0.2s ease;
                    margin-top: 20px;
                }
                .btn-plan-select:hover:not(:disabled) {
                    background: var(--primary);
                    color: white;
                    box-shadow: 0 8px 16px rgba(99, 102, 241, 0.3);
                }
                .btn-plan-select.current {
                    background: #f1f5f9;
                    border-color: #e2e8f0;
                    color: #94a3b8;
                    cursor: default;
                }
                .input-with-icon {
                    position: relative;
                }
                .input-icon {
                    position: absolute;
                    left: 15px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: var(--turnero-primary);
                    opacity: 0.5;
                }
                .input-with-icon .turnero-input {
                    padding-left: 45px;
                }
                .alert-icon-circle {
                    width: 50px;
                    height: 50px;
                    background: rgba(239, 68, 68, 0.2);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #ef4444;
                    font-size: 1.5rem;
                    flex-shrink: 0;
                }
            `}</style>
        </div>
    );
};

export default MySubscription;
